import { Capacitor } from "@capacitor/core";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonPage,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import React, { useMemo } from "react";
import DataController from "@controllers/DataController";
import { useHistory, useParams } from "react-router-dom";
import HeaderTitle from "@components/common/HeaderTitle";
import { useProgramData } from "@hooks/data/useProgramData";
import * as StringUtils from "@utils/stringUtils";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import { AllProgressCheckStatuses, IEvidence, IUser, ProgramSkill } from "@interfaces/Interfaces";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom, progressCheckDataAtom, userAtom } from "@state/State";
import _ from "lodash";
import * as ProgramUtils from "@utils/programUtils";
import SkillsRAGDisplay from "@components/programs/SkillsRAGDisplay";
import * as FirebaseService from "@controllers/FirebaseService";
import { EventRegister } from "react-native-event-listeners";
import classNames from "classnames";
import { Icon_Refresh } from "@assets/images";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";

const ProgramSkillsTopSectionPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string; skillSlug: string; topSectionSlug: string }>();

  const { userPrograms } = useProgramData();
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();

  const program = userPrograms.find((item) => item.ID === params.programId);
  const currentLevel = program ? ProgramUtils.getSkillLevelForProgram(user.contents, program) : "";

  const sectionSkills = ProgramUtils.filterSkillsByLevel(
    program?.Skills?.filter((item) => StringUtils.toSlug(item.Section ?? "") === params.skillSlug) ?? [],
    currentLevel
  );
  const skills = sectionSkills
    .filter((item) => StringUtils.toSlug(item["Top section"] ?? "") === params.topSectionSlug)
    .sort((a, b) => {
      const aParts = (a["Order Key"] ?? "").split(".").map(Number);
      const bParts = (b["Order Key"] ?? "").split(".").map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });
  const topSectionName = skills[0]?.["Top section"] ?? "";
  const topSectionDescription = skills.find((s) => s["Top section description"])?.["Top section description"] ?? "";

  const allEvidence = useRecoilValueLoadable<IEvidence[]>(evidenceAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[]>(progressCheckDataAtom);

  const isProgramLocked = useMemo(() => {
    if (!program || allProgressCheckData.state !== "hasValue" || allEvidence.state !== "hasValue") return false;
    const userLevel = ProgramUtils.getProgramLevel(user.contents?.programRoles ?? [], program.ID) ?? "";
    const isLocked = ProgramUtils.isUOCProgramLockedByEoYReview(
      program.ID,
      program.ProgressChecks ?? [],
      allProgressCheckData.contents,
      allEvidence.contents ?? [],
      userLevel
    );
    return isLocked;
  }, [program, allProgressCheckData, allEvidence]);

  const skillIds = new Set(skills.map((skill) => skill.ID));

  let evidence: IEvidence[] = [];
  if (allEvidence.state === "hasValue" && allEvidence.contents && program) {
    evidence = allEvidence.contents.filter((e: IEvidence) => {
      const programInfo = e.evidenceJSON?.programInfo;
      if (programInfo?.programID !== program.ID) return false;
      return skillIds.has(programInfo?.skillID);
    });
    evidence = _.orderBy(evidence, "date", "desc");
  }

  const nonDraftEvidence = evidence.filter((e) => e.draft !== true);
  const nonRejectedEvidence = nonDraftEvidence.filter((e) => e.evidenceJSON?.programInfo?.approvalState !== "Rejected");

  const skillAssessmentStatus = new Map(
    skills.map((skill) => {
      const skillEvidence = nonRejectedEvidence.filter((e) => e.evidenceJSON?.programInfo?.skillID === skill.ID);
      if (skillEvidence.some((e) => e.evidenceJSON?.programInfo?.assessmentType === "Summative")) {
        return [skill.ID, "Summative" as const];
      }
      if (skillEvidence.some((e) => e.evidenceJSON?.programInfo?.assessmentType === "Formative")) {
        return [skill.ID, "Formative" as const];
      }
      return [skill.ID, "Not Evidenced" as const];
    })
  );

  const skillEvidenceCounts = new Map(
    skills.map((skill) => [
      skill.ID,
      nonDraftEvidence.filter((e) => e.evidenceJSON?.programInfo?.skillID === skill.ID).length,
    ])
  );

  const evidenceCardClicked = (item: IEvidence): void => {
    history.push(`/dashboard/program/${params.programId}/read/${item.id}`);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}/program-skills-log/${params.skillSlug}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{topSectionName}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-[20px] w-[44px] text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <ProgramInfoCard
          title={topSectionName}
          fullWidthChildren
          subTitle={topSectionDescription}
          isHTMLSubtitle
          collapsible
          style={{ paddingBottom: 0 }}
        >
          <div className="mx-4 mb-3">
            <SkillsRAGDisplay
              skills={skills}
              evidence={nonDraftEvidence}
              showHeaderOnly
            />
          </div>
          {skills.map((skill) => (
            <SkillLogItem
              key={skill.ID}
              skill={skill}
              evidenceCount={skillEvidenceCounts.get(skill.ID) ?? 0}
              assessmentStatus={skillAssessmentStatus.get(skill.ID) ?? "Not Evidenced"}
              isLocked={isProgramLocked}
            />
          ))}
        </ProgramInfoCard>
        <ProgramInfoCard
          title="Evidence list"
          style={{ paddingTop: 14, paddingBottom: 8 }}
          subTitle={evidence.length === 0 ? "Any evidence added will appear below." : undefined}
        >
          {evidence.length > 0 && (
            <div className="mt-3">
              {evidence.map((item) => (
                <ProgramEvidenceCard
                  key={item.id}
                  buttonPressed={() => evidenceCardClicked(item)}
                  programEvidence={item}
                  program={program}
                  progressCheckLocked={false}
                />
              ))}
            </div>
          )}
        </ProgramInfoCard>
      </IonContent>
    </IonPage>
  );
};

type SkillLogItemProps = {
  skill: ProgramSkill;
  evidenceCount: number;
  assessmentStatus: "Summative" | "Formative" | "Not Evidenced";
  isLocked?: boolean;
};

export const SkillLogItem: React.FC<SkillLogItemProps> = (props) => {
  const params = useParams<{ programId: string; skillSlug: string }>();
  const { userPrograms } = useProgramData();

  const program = userPrograms.find((item) => item.ID === params.programId)!;

  const addSkillEvidence = (skill: ProgramSkill) => {
    const evidenceDefinitions = program?.["Evidence Definitions"] ?? [];

    // Find the evidence definition for this skill: first try by slug, then by matching
    // any definition ID referenced in the skill's own evidence parts.
    let evidenceDefinition = ProgramUtils.getSkillEvidenceDefinitionForSlug(program, params.skillSlug);
    if (!evidenceDefinition) {
      const defIDFromSkillParts = skill["Evidence Parts"]
        ?.flatMap((part) => part["Evidence Definitions"] ?? [])
        .find(Boolean);
      if (defIDFromSkillParts) {
        evidenceDefinition = evidenceDefinitions.find((d) => d.ID === defIDFromSkillParts);
      }
    }

    const data = {
      program: program,
      ...(program.CompetenceActivity &&
        program.CompetenceActivity.length > 0 && { competences: program.CompetenceActivity }),
      ...(evidenceDefinition && { evidenceDefinitions: evidenceDefinition }),
      ...(skill && { autoFilledSkill: skill }),
      preselectedTitle: skill["Display Name"] ?? skill.Name,
      skill: null,
      approvalState: "Pending",
    };

    FirebaseService.logEvent("program_add_skill_evidence", {
      program: program.Name,
      programID: program.ID,
      skill: skill.Name,
    });

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const getAssessmentStatus = (
    assessmentStatus: "Summative" | "Formative" | "Not Evidenced",
    evidenceCount: number
  ) => {
    switch (assessmentStatus) {
      case "Summative":
        return "Summative evidenced";
      case "Formative":
        return `Formative${evidenceCount > 1 ? "s" : ""} evidenced`;
      case "Not Evidenced":
        return "Not evidenced";
      default:
        return assessmentStatus;
    }
  };

  return (
    <IonItem
      button={!props.isLocked}
      detail={false}
      onClick={props.isLocked ? undefined : () => addSkillEvidence(props.skill)}
      className="preceptee-item-button flex"
      style={{ "--inner-padding-top": "4px", "--inner-padding-bottom": "4px" }}
    >
      <div className="flex w-full flex-row items-center justify-between gap-2 py-1 pl-safe-area-l pr-safe-area-r">
        <div className="flex flex-col gap-1">
          <div className="text-acc-15px font-acc-600 leading-default tracking-default text-grey-90">
            {props.skill["Display Name"] ?? props.skill.Name ?? ""}
          </div>
          {props.skill.Description && (
            <div className="text-acc-14px font-acc-500 leading-default tracking-default text-grey-60">
              {props.skill.Description ?? ""}
            </div>
          )}
          <div className="text-acc-11px leading-[1.27] text-grey-60">{`${props.evidenceCount} evidence`}</div>
          <div className="flex flex-row">
            <div
              className={classNames("flex items-center justify-center rounded-full px-2 py-1", {
                "bg-green-light-text": props.assessmentStatus === "Summative",
                "bg-orange-400": props.assessmentStatus === "Formative",
                "bg-grey-60": props.assessmentStatus === "Not Evidenced",
              })}
            >
              <span className="text-acc-11px font-semibold leading-none text-white">
                {getAssessmentStatus(props.assessmentStatus, props.evidenceCount)}
              </span>
            </div>
          </div>
        </div>
        <div
          className={classNames(
            "h-[20px] shrink-0 bg-transparent px-[8px] py-0 text-right text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-cta-blue",
            {
              "opacity-30": props.isLocked,
            }
          )}
        >
          {"Add"}
        </div>
      </div>
    </IonItem>
  );
};

export default ProgramSkillsTopSectionPage;
